import { TOKEN_KEYS } from '@/config/const';
import { GetMeResponse } from '@/types';
import { getMe } from '@/utils/Actions/getMe';
import { loginAction } from '@/utils/Actions/loginAction';
import { refreshTokenAction } from '@/utils/Actions/refreshTokenAction';
import { registerAction } from '@/utils/Actions/registerAction';
import { createContext, useContext, useEffect, useState, useCallback } from 'react';

interface User {
  email: string;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  user?: User;
  setAuthState: (authenticated: boolean, user: User) => void;
  signUp: (email: string, password: string) => Promise<{ success: boolean }>;
  signIn: (email: string, password: string) => Promise<{ user: GetMeResponse }>;
  signOut: () => void;
  getValidAccessToken: () => Promise<string | null>;
}

export const removeTokens = () => {
  Object.values(TOKEN_KEYS).forEach((key) => localStorage.removeItem(key));
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [authState, setAuthState] = useState<{
    isAuthenticated: boolean;
    isLoading: boolean;
    isRefreshing: boolean;
    user?: User;
  }>({
    isAuthenticated: false,
    isLoading: true,
    isRefreshing: false,
  });

  const updateAuthState = useCallback((isAuthenticated: boolean, user?: User): void => {
    setAuthState((prev) => ({ ...prev, isAuthenticated, isLoading: false, user }));
  }, []);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem(TOKEN_KEYS.refresh);
    const refreshExpireTime = Number(localStorage.getItem(TOKEN_KEYS.refreshExpire));

    if (!refreshToken || Date.now() / 1000 >= refreshExpireTime) {
      removeTokens();
      updateAuthState(false);
      return null;
    }

    try {
      setAuthState((prev) => ({ ...prev, isRefreshing: true }));

      const response = await refreshTokenAction(refreshToken);
      if (response?.accessToken) {
        localStorage.setItem(TOKEN_KEYS.access, response.accessToken);
        localStorage.setItem(TOKEN_KEYS.accessExpire, response.accessTokenExpireTime.toString());
        setAuthState((prev) => ({ ...prev, isRefreshing: false }));
        return response.accessToken;
      }
    } catch {
      removeTokens();
      updateAuthState(false);
    } finally {
      setAuthState((prev) => ({ ...prev, isRefreshing: false }));
    }

    return null;
  }, [updateAuthState]);

  const getValidAccessToken = useCallback(async (): Promise<string | null> => {
    const accessToken = localStorage.getItem(TOKEN_KEYS.access);
    const accessExpireTime = Number(localStorage.getItem(TOKEN_KEYS.accessExpire));

    if (!accessToken || Date.now() / 1000 >= accessExpireTime) {
      return refreshAccessToken();
    }
    return accessToken;
  }, [refreshAccessToken]);

  useEffect(() => {
    const initializeAuth = async () => {
      const accessToken = await getValidAccessToken();
      if (!accessToken) return updateAuthState(false);

      try {
        const user = await getMe(accessToken);
        updateAuthState(true, { email: user.email });
      } catch {
        removeTokens();
        updateAuthState(false);
      }
    };

    initializeAuth();
  }, [getValidAccessToken, updateAuthState]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const response = await loginAction(email, password);
      if (!response?.accessToken) {
        throw new Error('No active account found with the given credentials');
      }

      const user = await getMe(response.accessToken);

      localStorage.setItem(TOKEN_KEYS.access, response.accessToken);
      localStorage.setItem(TOKEN_KEYS.accessExpire, response.accessTokenExpireTime.toString());
      localStorage.setItem(TOKEN_KEYS.refresh, response.refreshToken);
      localStorage.setItem(TOKEN_KEYS.refreshExpire, response.refreshTokenExpireTime.toString());

      updateAuthState(true, { email: user.email });

      return { user };
    },
    [updateAuthState]
  );

  const signUp = useCallback(async (email: string, password: string) => {
    await registerAction(email, password);
    return { success: true };
  }, []);

  const signOut = useCallback(() => {
    removeTokens();
    updateAuthState(false);
  }, [updateAuthState]);

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        setAuthState: updateAuthState,
        signUp,
        signIn,
        signOut,
        getValidAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextValue => {
  const context = useContext(AuthContext) as AuthContextValue;
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
